"use client";

import React from "react";
import { Phone, Mail, ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function QuoteCTA() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Clapham",
    to: "/AreasWeCoverPage"
  }, {
    name: "Wandsworth",
    to: "/AreasWeCoverPage"
  }];
  const trustPoints = ["NASC accredited", "CHAS certified"];
  return <section id="finalquotecta" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} style={{
        opacity: 0
      }}>
          <Card className="border border-border bg-card text-card-foreground shadow-xl rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 sm:p-10 lg:p-12 items-center">
              
              {/* Main CTA copy */}
              <div className="lg:col-span-7 space-y-6">
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge variant="outline" className="border-border text-foreground font-medium px-3 py-1">
                    Safe. Reliable. Professional.
                  </Badge>
                </div>

                <div className="space-y-3">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
                    Request a scaffolding quote
                  </h2>
                  <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                    Speak directly with our estimating team for project pricing, site surveys, and straightforward technical advice across London and the Home Counties.
                  </p>
                </div>

                {/* Accreditations Trust Strip */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 border-t border-border">
                  {trustPoints.map((point, i) => <div key={point} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-medium">
                      <ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                      <span>{point}</span>
                    </div>)}
                </div>

                {/* Nearby Areas */}
                <div className="pt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5 font-medium text-foreground">
                    <MapPin className="h-4 w-4 text-primary shrink-0" />
                    <span>Nearby areas:</span>
                  </div>
                  {nearbyAreas.map((area, index) => <span key={area.name} data-index={index} className="inline-flex items-center">
                      <Link to={area.to} className="text-foreground hover:text-primary underline underline-offset-4 transition-colors font-medium">
                        {area.name}
                      </Link>
                      {index < nearbyAreas.length - 1 && <span className="ml-2 text-border">|</span>}
                    </span>)}
                </div>
              </div>

              {/* Actions & Direct Contacts */}
              <div className="lg:col-span-5 flex flex-col gap-4 bg-muted/40 p-6 sm:p-8 rounded-xl border border-border">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base h-12">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>Get a quote online</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-muted px-2 text-muted-foreground font-medium">Or reach us directly</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <a href="tel:01344929900" className="flex items-center gap-3.5 p-3.5 rounded-lg border border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground transition-colors group">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground group-hover:text-muted-foreground font-medium">Call contracts office</div>
                      <div className="text-base font-semibold tracking-tight text-foreground group-hover:text-accent-foreground">01344 929900</div>
                    </div>
                  </a>

                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-3.5 p-3.5 rounded-lg border border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground transition-colors group">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground font-medium">Email drawing & specs</div>
                      <div className="text-sm sm:text-base font-semibold tracking-tight text-foreground truncate">
                        quotes@bmfscaffolding.co.uk
                      </div>
                    </div>
                  </a>
                </div>
              </div>

            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}